/**
 * LCA module API client.
 *
 * Wraps axios with the platform's REACT_APP_BACKEND_URL + /api/lca prefix and
 * a JWT interceptor that mirrors the platform's existing axios usage.
 *
 * Usage:
 *   import lcaClient from '../../services/lcaClient';
 *   const { data } = await lcaClient.get('/projects', { params: { site_id } });
 *   await lcaClient.post('/projects', { name, description });
 */

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const lcaClient = axios.create({
  baseURL: `${BACKEND_URL}/api/lca`,
  headers: { 'Content-Type': 'application/json' },
});

lcaClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

lcaClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default lcaClient;

// ===== CSV import/template helpers =====
//
// Shared by the two LCA import UIs (DatasetLibrary and the Phase 2 inventory
// hook) so their feedback stays consistent.

/**
 * FastAPI returns `detail` as a string for an explicit HTTPException but as an
 * array of objects for request-validation errors. sonner cannot render an
 * object, so coerce whatever shape arrived into text.
 */
export function detailToText(detail) {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
  }
  return detail.msg || JSON.stringify(detail);
}

/** Message for a failed upload, falling back when the server sent no detail. */
export function uploadErrorMessage(error, fallback = 'Failed to upload CSV') {
  return detailToText(error?.response?.data?.detail) || fallback;
}

/**
 * Toast body for an import that was rejected. The backend returns the actual
 * per-row messages in `errors`; showing only `errors.length` used to leave the
 * user with no idea which line to fix.
 */
export function importErrorSummary(errors, limit = 5) {
  const shown = (errors || []).slice(0, limit).join('\n');
  const rest = (errors || []).length - limit;
  return rest > 0 ? `${shown}\n...and ${rest} more` : shown;
}

/**
 * Download an LCA CSV template to disk.
 *
 * `fetch(...).then(r => r.blob())` resolves for 4xx/5xx too, so without the
 * ok-check an error body was saved as a .csv next to a "downloaded" toast.
 * The anchor is attached to the document because a click on a detached node is
 * ignored by some browsers.
 */
export async function downloadLcaTemplate(path, filename) {
  const token = localStorage.getItem('token');
  const response = await fetch(`${BACKEND_URL}/api/lca${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    let detail = '';
    try {
      detail = detailToText((await response.json())?.detail);
    } catch {
      detail = '';
    }
    throw new Error(detail || `HTTP ${response.status}`);
  }
  const url = URL.createObjectURL(await response.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
